import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import LegalLinks from '@/components/LegalLinks';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  AppButton,
  AppButtonRow,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppContentContainer,
  AppFieldGroup,
  AppInlineAlert,
  AppInput,
  AppSectionHeader,
  AppShell,
  AppTextLinkButton,
  AppText,
} from '../../ui-foundation';
import { AppCheckbox as Checkbox } from '../../ui-foundation/adapters/web';

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M14.26 9.55c-.02-2.29 1.87-3.4 1.96-3.45-1.07-1.57-2.74-1.78-3.33-1.81-1.42-.14-2.77.84-3.49.84-.72 0-1.83-.82-3.01-.8-1.54.02-2.97.9-3.76 2.28-1.6 2.78-.41 6.9 1.15 9.16.77 1.11 1.68 2.35 2.88 2.31 1.16-.05 1.59-.75 2.99-.75 1.4 0 1.79.75 3.01.72 1.24-.02 2.03-1.13 2.79-2.24.88-1.28 1.24-2.52 1.26-2.58-.03-.01-2.43-.93-2.45-3.68zM11.98 2.9c.64-.78 1.07-1.86.95-2.94-.92.04-2.03.61-2.69 1.38-.59.68-1.11 1.77-.97 2.82 1.02.08 2.06-.52 2.71-1.26z" fill="currentColor"/>
  </svg>
);

const SPECIAL_CATEGORY_CONSENT_VERSION = '2026-04-03';
const SPECIAL_CATEGORY_CONSENT_STORAGE_KEY = 'adulting.special-category-consent.pending';

function buildSpecialCategoryConsentRecord(source: string) {
  const acceptedAt = new Date().toISOString();

  return {
    special_category_consent_at: acceptedAt,
    special_category_consent_version: SPECIAL_CATEGORY_CONSENT_VERSION,
    special_category_consent_source: source,
  };
}

export default function AuthPage() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [oauthReturning, setOauthReturning] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [hasAcceptedLegal, setHasAcceptedLegal] = useState(false);
  const [hasAcceptedSpecialCategoryConsent, setHasAcceptedSpecialCategoryConsent] = useState(false);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    const hashParams = new URLSearchParams(currentUrl.hash.replace(/^#/, ''));
    const errorMessage =
      currentUrl.searchParams.get('error_description') ||
      hashParams.get('error_description') ||
      currentUrl.searchParams.get('error') ||
      hashParams.get('error');

    const returningFromOAuth =
      currentUrl.searchParams.has('code') ||
      hashParams.has('access_token') ||
      hashParams.has('refresh_token') ||
      hashParams.has('provider_token');

    if (errorMessage) {
      const decoded = decodeURIComponent(errorMessage.replace(/\+/g, ' '));
      setOauthError(decoded);
      setOauthReturning(false);
      toast.error(decoded);
      return;
    }

    if (returningFromOAuth) {
      setOauthReturning(true);
      const timer = window.setTimeout(() => {
        setOauthReturning(false);
      }, 8000);

      return () => window.clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function applyPendingSpecialCategoryConsent() {
      const pendingRaw = window.localStorage.getItem(SPECIAL_CATEGORY_CONSENT_STORAGE_KEY);
      if (!pendingRaw) {
        return;
      }

      let pendingData: Record<string, string> | null = null;

      try {
        pendingData = JSON.parse(pendingRaw) as Record<string, string>;
      } catch {
        window.localStorage.removeItem(SPECIAL_CATEGORY_CONSENT_STORAGE_KEY);
        return;
      }

      const startedAt = Date.now();

      while (!cancelled && Date.now() - startedAt < 8000) {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { error } = await supabase.auth.updateUser({ data: pendingData });
          if (!error) {
            window.localStorage.removeItem(SPECIAL_CATEGORY_CONSENT_STORAGE_KEY);
          }
          return;
        }

        await new Promise((resolve) => window.setTimeout(resolve, 400));
      }
    }

    void applyPendingSpecialCategoryConsent();

    return () => {
      cancelled = true;
    };
  }, []);

  const requireLegalAcceptance = () => {
    if (!isSignUp) {
      return true;
    }

    if (!hasAcceptedLegal) {
      toast.error('Please agree to the Terms and Privacy policy before creating your account.');
      return false;
    }

    if (!hasAcceptedSpecialCategoryConsent) {
      toast.error('Please confirm the optional sensitive-data consent if you want to create an Adulting account.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!requireLegalAcceptance()) return;
    const trimmedFirstName = firstName.trim();
    if (isSignUp && !trimmedFirstName) {
      toast.error('Enter your first name so Adulting can set up your profile properly.');
      return;
    }
    setLoading(true);
    try {
      if (isSignUp) {
        const acceptedAt = new Date().toISOString();
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              given_name: trimmedFirstName,
              terms_accepted_at: acceptedAt,
              privacy_accepted_at: acceptedAt,
              terms_version: '2026-03-26',
              privacy_version: '2026-03-26',
              agreement_source: 'web-auth',
              ...buildSpecialCategoryConsentRecord('web-signup'),
            },
          },
        });
        if (error) throw error;
        if (data.session) {
          toast.success('Account created. You are signed in.');
        } else {
          toast.success('Check your email to confirm your account!');
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
    } catch (err: any) {
      toast.error(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: 'google' | 'apple') => {
    if (!requireLegalAcceptance()) return;
    setOauthError(null);
    setSocialLoading(provider);

    try {
      const redirectTo = new URL('/auth', window.location.origin).toString();
      if (isSignUp) {
        window.localStorage.setItem(
          SPECIAL_CATEGORY_CONSENT_STORAGE_KEY,
          JSON.stringify(buildSpecialCategoryConsentRecord('web-oauth-signup')),
        );
      }
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
          skipBrowserRedirect: true,
          ...(provider === 'google'
            ? { queryParams: { prompt: 'select_account' } }
            : {}),
        },
      });

      if (error) throw error;
      if (!data?.url) {
        throw new Error(`Could not start ${provider === 'google' ? 'Google' : 'Apple'} sign-in.`);
      }

      window.location.assign(data.url);
    } catch (err: any) {
      setSocialLoading(null);
      toast.error(err.message || `${provider === 'google' ? 'Google' : 'Apple'} sign-in failed`);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      toast.error('Enter your email first, then tap reset password.');
      return;
    }

    setResetSending(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetSending(false);

    if (error) {
      toast.error(error.message || 'Failed to send reset email');
      return;
    }

    toast.success('Password reset email sent. Check your inbox.');
  };

  return (
    <AppShell className="min-h-screen justify-center px-4 py-6 sm:py-10">
      <AppContentContainer maxWidth={540} className="w-full animate-fade-in">
        <AppCard className="rounded-[32px] border-border bg-card/95 p-4 shadow-sm sm:p-5">
          <div className="rounded-[28px] border border-primary/10 bg-[linear-gradient(180deg,rgba(107,53,102,0.08),rgba(255,255,255,0.92))] px-5 py-6 text-center sm:px-7 sm:py-7">
            <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-[24px] bg-background shadow-sm ring-1 ring-border/70 sm:h-24 sm:w-24">
              <img src="/adulting-logo.png" alt="Adulting" className="h-14 bg-transparent sm:h-16" />
            </div>
            <AppText className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary/70">Adulting LifeOS</AppText>
            <h1 className="mt-4 text-[1.95rem] font-semibold leading-tight text-foreground sm:text-[2.15rem]">
              {isSignUp ? "Welcome - we're really glad you're here" : 'Welcome back to Adulting'}
            </h1>
            <AppText className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
              {isSignUp
                ? 'Nobody hands you a manual for adult life. Adulting gives you one calm place for money, home, health, family, work, calendar, and documents.'
                : 'Your calm corner for the renewals, deadlines, forms, reminders, and admin you do not want to rebuild from scratch.'}
            </AppText>
            <div className="mt-4 rounded-2xl border border-primary/10 bg-background/85 px-4 py-3">
              <AppText className="text-sm font-medium text-foreground">
                {isSignUp ? 'Start with one real task today. Everything else can wait.' : 'Pick things back up at your own pace. Everything is ready when you are.'}
              </AppText>
              <AppText className="mt-1 text-xs leading-relaxed text-muted-foreground">
                {isSignUp ? 'Free during beta, with no card required.' : 'Sign in with email, Google, or Apple.'}
              </AppText>
            </div>
          </div>

          <AppCard className="mt-4 rounded-[28px] border-border bg-background/80 p-5 shadow-sm sm:p-6">
            {oauthReturning ? (
              <AppInlineAlert title="Finishing sign in..." tone="info">
                <span className="inline-flex items-start gap-3">
                  <Loader2 size={16} className="mt-0.5 shrink-0 animate-spin text-primary" />
                  <span>If nothing happens in a few seconds, come back and try Google, Apple, or password sign-in again.</span>
                </span>
              </AppInlineAlert>
            ) : null}

            {oauthError ? (
              <AppInlineAlert title="Sign-in could not be completed" tone="error">
                {oauthError}
              </AppInlineAlert>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-4">
              <AppCardHeader className="space-y-1">
                <AppSectionHeader
                  title={isSignUp ? 'Create your account' : 'Sign in with email'}
                  subtitle={
                    isSignUp
                      ? 'Set up your login details first, then choose the first area of life you want help with.'
                      : 'Use the same login details you used last time, or choose a social sign-in below.'
                  }
                />
              </AppCardHeader>

              <AppFieldGroup label="Email address">
                <AppInput
                  id="auth-email"
                  type="email"
                  value={email}
                  onChangeText={setEmail}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  inputMode="email"
                />
              </AppFieldGroup>

              {isSignUp ? (
                <AppFieldGroup label="First name">
                  <AppInput
                    id="auth-first-name"
                    value={firstName}
                    onChangeText={setFirstName}
                    placeholder="Your first name"
                    required
                    autoComplete="given-name"
                    autoCorrect={false}
                    spellCheck={false}
                  />
                </AppFieldGroup>
              ) : null}

              <AppFieldGroup label="Password">
                <AppInput
                  id="auth-password"
                  type="password"
                  value={password}
                  onChangeText={setPassword}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                />
              </AppFieldGroup>

              {!isSignUp ? (
                <AppCard className="rounded-2xl border-border bg-muted/35 px-4 py-3">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <AppButton type="button" tone="ghost" size="sm" onPress={handleResetPassword} disabled={resetSending}>
                      {resetSending ? 'Sending reset...' : 'Forgot password?'}
                    </AppButton>
                    <AppText className="text-xs leading-relaxed text-muted-foreground sm:max-w-[220px] sm:text-right">
                      Migrated users should reset their password first.
                    </AppText>
                  </div>
                </AppCard>
              ) : null}

              {isSignUp ? (
                <AppCard className="rounded-2xl border-border bg-muted/35 px-4 py-3">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="auth-legal-agreement"
                      checked={hasAcceptedLegal}
                      onCheckedChange={(checked) => setHasAcceptedLegal(checked === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="auth-legal-agreement" className="text-sm leading-relaxed text-muted-foreground">
                      I agree to the <Link to="/terms" className="text-primary hover:underline">Terms</Link> and <Link to="/privacy" className="text-primary hover:underline">Privacy policy</Link> so Adulting can create my account. You can also review <Link to="/gdpr" className="text-primary hover:underline">GDPR &amp; data rights</Link> before you continue.
                    </label>
                  </div>
                  <div className="mt-3 flex items-start gap-3">
                    <Checkbox
                      id="auth-special-category-consent"
                      checked={hasAcceptedSpecialCategoryConsent}
                      onCheckedChange={(checked) => setHasAcceptedSpecialCategoryConsent(checked === true)}
                      className="mt-0.5"
                    />
                    <label htmlFor="auth-special-category-consent" className="text-sm leading-relaxed text-muted-foreground">
                      If I choose to use Adulting for optional health information, dependant details, or similarly sensitive uploaded documents, I explicitly consent to Adulting processing that information so those features can work for me.
                    </label>
                  </div>
                </AppCard>
              ) : null}

              <AppButton
                type="submit"
                size="lg"
                width="100%"
                disabled={loading || socialLoading !== null || (isSignUp && (!hasAcceptedLegal || !hasAcceptedSpecialCategoryConsent || !firstName.trim()))}
              >
                {loading ? 'Please wait...' : isSignUp ? 'Create account' : 'Sign in'}
              </AppButton>

              <div className="relative flex items-center gap-3 py-1">
                <div className="flex-1 border-t border-border" />
                <AppText className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  or continue with
                </AppText>
                <div className="flex-1 border-t border-border" />
              </div>

              <AppButtonRow flexDirection="column" alignItems="stretch">
                <AppButton
                  type="button"
                  tone="outline"
                  size="lg"
                  onPress={() => void handleSocialSignIn('google')}
                  disabled={loading || resetSending || socialLoading !== null || (isSignUp && (!hasAcceptedLegal || !hasAcceptedSpecialCategoryConsent))}
                  justifyContent="center"
                  width="100%"
                >
                  {socialLoading === 'google' ? (
                    <Loader2 size={16} className="animate-spin text-primary" />
                  ) : (
                    <GoogleIcon />
                  )}
                  {socialLoading === 'google' ? 'Opening Google...' : 'Continue with Google'}
                </AppButton>

                <AppButton
                  type="button"
                  tone="outline"
                  size="lg"
                  onPress={() => void handleSocialSignIn('apple')}
                  disabled={loading || resetSending || socialLoading !== null || (isSignUp && (!hasAcceptedLegal || !hasAcceptedSpecialCategoryConsent))}
                  justifyContent="center"
                  width="100%"
                >
                  {socialLoading === 'apple' ? (
                    <Loader2 size={16} className="animate-spin text-primary" />
                  ) : (
                    <AppleIcon />
                  )}
                  {socialLoading === 'apple' ? 'Opening Apple...' : 'Continue with Apple'}
                </AppButton>
              </AppButtonRow>

              <AppCard className="rounded-2xl bg-card px-4 py-3 text-center">
                <AppText className="text-sm leading-relaxed text-muted-foreground">
                  {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                  <AppTextLinkButton onClick={() => setIsSignUp(!isSignUp)}>
                    {isSignUp ? 'Sign in' : 'Sign up'}
                  </AppTextLinkButton>
                </AppText>
              </AppCard>

              {!isSignUp ? (
                <AppText className="text-center text-sm leading-relaxed text-muted-foreground">
                  If you have turned on two-step verification, Adulting will ask for your authenticator code after password sign-in.
                </AppText>
              ) : (
                <AppText className="text-center text-sm leading-relaxed text-muted-foreground">
                  Google or Apple can also create your account here once you have ticked the agreement above.
                </AppText>
              )}

              <LegalLinks className="pt-1" tone="public" />
            </form>
          </AppCard>

          <AppCard className="mt-4 rounded-[24px] border-border bg-card/80 px-5 py-4 shadow-sm">
            <LegalLinks />
          </AppCard>
        </AppCard>
      </AppContentContainer>
    </AppShell>
  );
}
